import React, { useMemo, useState, useEffect } from "react";
import { Alert, Schema } from "rsuite";

import { checkPermissionsFor } from "store/storeHelpers";
import {
    ACCESS_LIST_FILTERS_MODIFY_NOTIFICATION_DIALER_API,
    ACCESS_LIST_FILTERS_CREATE_DIALER_API,
    ACCESS_LIST_FILTERS_MODIFY_DIALER_API,
    ACCESS_LIST_FILTERS_REMOVE_DIALER_API
} from "const/apiMethods";
import { useWindowWidth } from "hooks";
import { deleteNullsFromObj, hasErrorObject } from "utils";
import AccessListFiltersSettings from "./AccessListFiltersSettings/AccessListFiltersSettings";
import AccessListFiltersFilter from "./AccessListFiltersFilter/AccessListFiltersFilter";
import AccessListFiltersTable from "./AccessListFiltersTable/AccessListFiltersTable";
import "./AccessListFilters.css";

const { StringType, NumberType } = Schema.Types;


const AccessListFilters = ({
    data,
    service,
    accountId,
    roleList,
    loading,
    worldzoneList,
    destinationList,
    subdestinationList,
    notificationSettings,
    numberTypeFamilyList,

    getItems,
    createApiFunc,
    modifyApiFunc,
    removeApiFunc,
    getAuthNumberType,
    getNotificationSettings,
    unsetNotificationSettings,
    modifyNotificationSettings
}) => {
    const [filter, setFilter] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    // const [showModalItemRemove, setShowModalItemRemove] = useState(false);
    const [editKeys, setEditKeys] = useState([]);
    const [serviceCreateValue, setServiceCreateValue] = useState(null);

    const windowWidth = useWindowWidth();

    const formModel = useMemo(() => {
        return Schema.Model({
            a_wz_key: NumberType(),
            a_de_key: NumberType(),
            a_sde_key: NumberType(),
            ...(service
                ? {
                    a_prefix: StringType()
                        .pattern(/^[0-9][\d]*$/, "The number must contain only digits")
                        .minLength(6, "Min length is 6 characters")
                        .maxLength(15, "Max length is 15 characters")
                }
                : {
                    a_prefix: StringType().maxLength(15, "Max length is 15 characters")
                }
            ),
            b_wz_key: NumberType(),
            b_de_key: NumberType(),
            b_sde_key: NumberType(),
            rate_min: NumberType()
        });
    }, [service]);

    useEffect(() => {
        getItems(accountId);
        getNotificationSettings(accountId);

        if (service) {
            getAuthNumberType();
        }
    }, [service, accountId]);

    useEffect(() => {
        return () => {
            unsetNotificationSettings();
        };
    }, []);

    const onDataUpdate = () => {
        getItems(accountId);
    };

    const handleCreate = async (data) => {
        const clearedData = deleteNullsFromObj(data);

        const finalParams = {
            ...clearedData,
            rate_min: +clearedData.rate_min
        };

        if (!finalParams.a_prefix && finalParams.a_prefix !== undefined) {
            delete finalParams.a_prefix;
        }

        try {
            const response = await createApiFunc(service, accountId, finalParams);

            if (response?.access_list_filter?.alf_key) {
                Alert.success("Access list filter created!");
            }

            return response;
        } catch (error) {
            throw error;
        } finally {
            setServiceCreateValue(null);
        }
    };

    const handleModify = async (params, rowData) => {
        const checkedForm = formModel.check(params);

        const hasErrorData = hasErrorObject(checkedForm);

        if (hasErrorData) {
            return;
        }

        const finalParams = {
            ...params,
            rate_min: +params.rate_min
        };


        if (!finalParams.a_prefix && finalParams.a_prefix !== undefined) {
            delete finalParams.a_prefix;
        }

        try {
            const response = await modifyApiFunc(rowData.alf_key, finalParams);

            Alert.success("Access list filter modified!");

            return response;
        } catch (error) {
            throw error;
        }
    };

    const handleRemove = async (key) => {
        try {
            const response = await removeApiFunc(key);

            Alert.success("Access list filter removed!");

            return response;
        } catch (error) {
            throw error;
        }
    };


    const onChangeFilter = (filter) => {
        setFilter(filter);
        setIsLoading(false);
    };

    const supervisorSmsRole = roleList.includes("Supervisor (sms)");
    const isSupervisorSms = roleList && roleList.length ? supervisorSmsRole : null;
    const showSaveButton = !isSupervisorSms || !!isSupervisorSms && checkPermissionsFor(ACCESS_LIST_FILTERS_MODIFY_NOTIFICATION_DIALER_API);

    return (
        <div>
            <AccessListFiltersSettings
                accountId={accountId}
                handleSubmit={modifyNotificationSettings}
                settings={notificationSettings}
                showSaveButton={showSaveButton}
            />

            <AccessListFiltersFilter
                onChange={onChangeFilter}
                defaultFilters={filter}
                service={service}
                {...{
                    numberTypeFamilyList
                }}
            />

            <AccessListFiltersTable
                filter={filter}
                accountId={accountId}
                data={data}
                loading={loading || isLoading}
                // onItemShowEdit={handleFilterShowEdit}
                service={service}
                formModel={formModel}
                windowWidth={windowWidth}

                onDataUpdate={onDataUpdate}
                onRowCreate={handleCreate}
                onRowSubmit={handleModify}
                onRowRemove={handleRemove}

                createPermission={checkPermissionsFor(ACCESS_LIST_FILTERS_CREATE_DIALER_API)}
                modifyPermission={checkPermissionsFor(ACCESS_LIST_FILTERS_MODIFY_DIALER_API)}
                removePermission={checkPermissionsFor(ACCESS_LIST_FILTERS_REMOVE_DIALER_API)}

                {...{
                    editKeys,
                    setEditKeys,
                    worldzoneList,
                    destinationList,
                    subdestinationList,
                    numberTypeFamilyList,
                    serviceCreateValue,
                    setServiceCreateValue
                }}
            />
        </div>
    );
};

export default AccessListFilters;